// components
import EmployeeSelector from 'components/shared/EmployeeSelector';

// hooks
import { useMemo } from 'react';
import useGetUsersEmployees from 'components/flex-portal/FlexMatch/hooks/useGetUsersEmployees';

// utils
import { startOfDay } from 'date-fns';

// types
import type { Employee } from 'components/shared/EmployeeSelector';
import type { RegisterAbsenceDialogEmployee } from './RegisterAbsenceDialog.types';

type AbsenceEmployeeFieldProps = {
  /** the list is only fetched while the field can actually be used */
  enabled: boolean;
  onSelect: (employee: RegisterAbsenceDialogEmployee) => void;
  autoFocus?: boolean;
};

/**
 * The employee to register an absence for, chosen from the employees within
 * the logged-in account's data rights. Shared by the register dialog itself
 * and the Dojo flow's picker dialog.
 */
const AbsenceEmployeeField = ({
  enabled,
  onSelect,
  autoFocus
}: AbsenceEmployeeFieldProps) => {
  // the date is part of the query key, so it has to stay stable between renders:
  // a fresh `new Date()` per render keeps invalidating the query and refetching
  const referenceDate = useMemo(() => startOfDay(new Date()), []);
  const { data: employees } = useGetUsersEmployees(
    { date: referenceDate, onlyFlex: false },
    { enabled }
  );

  const options: Employee[] = (employees || []).map((employee) => ({
    ...employee,
    id: employee.empId,
    employeeId: employee.empId,
    employeeName: employee.nameInformal,
    label: `${employee.nr} - ${
      employee.nameCompoundSurname?.replace(/\r?\n|\r/, '') ?? ''
    }`
  }));

  return (
    <EmployeeSelector
      options={options}
      fullWidth
      variant="outlined"
      autoFocus={autoFocus}
      onSelected={(employee) => {
        if (employee?.id) {
          onSelect({
            id: employee.id,
            name: employee.employeeName ?? employee.label,
            number: employee.nr
          });
        }
      }}
    />
  );
};

export default AbsenceEmployeeField;
